import { getApps, initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  enableIndexedDbPersistence,
  getFirestore
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

const E0_PROJECT_ID = 'demo-eznotemanager-e0';
const EMULATOR_HOST = '127.0.0.1';
const AUTH_EMULATOR_PORT = 9099;
const FIRESTORE_EMULATOR_PORT = 8080;
const FUNCTIONS_EMULATOR_PORT = 5001;

const usesEmulators = () =>
  process.env.NODE_ENV === 'test'
  || (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true');

const productionConfig = () => ({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
});

const assertCompleteConfig = (config) => {
  const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];
  if (requiredKeys.some((key) => !config[key])) {
    throw new Error('Firebase configuration is incomplete; refusing to initialize a non-emulator client.');
  }
};

const initializeAppCheckIfConfigured = (app, emulatorMode) => {
  const siteKey = process.env.REACT_APP_FIREBASE_APP_CHECK_SITE_KEY;
  if (emulatorMode) {
    return;
  }

  if (!siteKey) {
    if (process.env.NODE_ENV === 'production') {
      console.warn('Firebase App Check is not configured; AI summaries will be unavailable until it is configured.');
    }
    return;
  }

  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true
  });
};

let services;

export const initializeFirebase = () => {
  if (services) {
    return services;
  }

  const emulatorMode = usesEmulators();
  const config = emulatorMode
    ? { apiKey: 'demo-api-key', authDomain: 'localhost', projectId: E0_PROJECT_ID }
    : productionConfig();

  if (!emulatorMode) {
    assertCompleteConfig(config);
  }

  const app = getApps().find((existingApp) => existingApp.options.projectId === config.projectId)
    || initializeApp(config);
  initializeAppCheckIfConfigured(app, emulatorMode);
  const auth = getAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app, config.storageBucket);
  const functions = getFunctions(app, process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'europe-west3');

  if (emulatorMode) {
    connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${AUTH_EMULATOR_PORT}`, {
      disableWarnings: true
    });
    connectFirestoreEmulator(db, EMULATOR_HOST, FIRESTORE_EMULATOR_PORT);
    connectFunctionsEmulator(functions, EMULATOR_HOST, FUNCTIONS_EMULATOR_PORT);
  }

  enableIndexedDbPersistence(db).catch(() => {
    // Persistence is unavailable in some browser contexts (for example, another open tab).
  });

  services = { app, auth, db, storage, functions };
  return services;
};

const firebase = initializeFirebase();

export const { app, auth, db, storage, functions } = firebase;
export default app;
